import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  TodoPersonalItemResponse,
  TodoPersonalSectionResponse,
  TodoProjectResponse,
} from '@/lib/api';
import { usePersonalChecklist } from '../hooks';
import { ChecklistEditor } from './ChecklistEditor';
import { ProjectPicker } from './ProjectPicker';
import { formatCreatedDate } from '../lib/dates';

const NO_SECTION = 'none';

interface EditPersonalItemDialogProps {
  open: boolean;
  onClose: () => void;
  item: TodoPersonalItemResponse | null;
  sections: TodoPersonalSectionResponse[];
  holderId: string | null;
  listId: string;
  projects?: TodoProjectResponse[];
  onCreateProject?: (name: string) => Promise<TodoProjectResponse>;
  onSave: (
    id: string,
    input: {
      title?: string;
      description?: string | null;
      personalSectionId?: string | null;
      projectId?: string | null;
    },
  ) => void;
}

/**
 * Edit a private "My Tasks" item — details, the private section it sits in, and
 * its checklist. Mirrors the shared list's EditItemDialog so managing a personal
 * task feels the same, but every field maps to the per-user overlay tables.
 */
export function EditPersonalItemDialog({
  open,
  onClose,
  item,
  sections,
  holderId,
  listId,
  projects = [],
  onCreateProject,
  onSave,
}: EditPersonalItemDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState<string>(NO_SECTION);
  const [projectId, setProjectId] = useState<string | null>(null);

  // The board only ever surfaces top-level items; the checklist lives on those.
  const isTopLevel = !!item && item.parentPersonalItemId === null;
  const {
    items: checklistItems,
    createItem,
    setDone,
    deleteItem,
    reorder,
  } = usePersonalChecklist(
    holderId,
    listId,
    isTopLevel ? (item?.personalItemId ?? null) : null,
    open,
  );
  const checklistTotal = checklistItems.length;
  const checklistDone = checklistItems.filter((c) => c.isDone).length;

  useEffect(() => {
    if (open && item) {
      setTitle(item.title);
      setDescription(item.description ?? '');
      setSectionId(item.personalSectionId ?? NO_SECTION);
      setProjectId(item.projectId ?? null);
    }
  }, [open, item]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!item || !title.trim()) return;
    const newSection = sectionId === NO_SECTION ? null : sectionId;
    const oldSection = item.personalSectionId ?? null;
    const oldProject = item.projectId ?? null;
    onSave(item.personalItemId, {
      title: title.trim(),
      description: description.trim() || null,
      ...(newSection !== oldSection ? { personalSectionId: newSection } : {}),
      ...(projectId !== oldProject ? { projectId } : {}),
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-md flex-col">
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogDescription>Update your private item. Only you can see it.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
          {/* Scrolls when the content (esp. a long checklist) exceeds the viewport
              — keeps the header + footer pinned. Sized in dvh for mobile. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="edit-personal-item-title">Title</Label>
              <Input
                id="edit-personal-item-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-personal-item-description">Description (optional)</Label>
              <Textarea
                id="edit-personal-item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Section</Label>
              <Select value={sectionId} onValueChange={setSectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Unsectioned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SECTION}>Unsectioned</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.personalSectionId} value={s.personalSectionId}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isTopLevel && (
              <div className="space-y-2">
                <Label>Project</Label>
                <ProjectPicker
                  value={projectId}
                  projects={projects}
                  onChange={setProjectId}
                  onCreateProject={onCreateProject}
                />
              </div>
            )}

            {isTopLevel && (
              <ChecklistEditor
                items={checklistItems.map((c) => ({
                  id: c.personalChecklistItemId,
                  title: c.title,
                  done: c.isDone,
                }))}
                doneCount={checklistDone}
                totalCount={checklistTotal}
                onToggle={(id, done) => setDone(id, !done)}
                onDelete={(id) => deleteItem(id)}
                onAdd={(t) => createItem(t)}
                onReorder={(order) => reorder(order)}
              />
            )}

            {item && (
              <p className="pt-1 text-xs text-muted-foreground">
                Created {formatCreatedDate(item.createdAt)}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
